import { action, flow, makeObservable, observable } from 'mobx';
import { ApiResponse, GlobalStore, GridStore } from '@roc/feature-app-core';
import { LenderLicensesService } from '../services/lenderLicensesService';

export abstract class LenderLicensesBaseStore {
  globalStore: GlobalStore;
  service: LenderLicensesService;
  lenderLicensesGridStore: GridStore;
  documentPreview: { title: string; data: any; headers: any } | null = null;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.service = new LenderLicensesService();

    this.lenderLicensesGridStore = new GridStore(
      () => this.fetchLenderLicensesGrid(),
      null,
      50
    );

    makeObservable(this, {
      documentPreview: observable,
      reset: action,
      closeDocumentPreview: action,
      resetAndFetchGridData: flow,
      fetchOnlyGridData: flow,
      previewLicenseDocument: flow,
    });
  }

  closeDocumentPreview() {
    this.documentPreview = null;
  }

  private async fetchLenderLicensesGrid(): Promise<ApiResponse> {
    try {
      const meta = this.lenderLicensesGridStore.gridData.meta;
      const filters = { ...(meta.filters || {}), ...(meta.dropdownFilters || {}) };
      const pageNumber = meta.pageNumber;
      const pageSize = meta.pageSize;
      const sortBy = meta.sortBy;
      const sortDir = meta.sortDir;

      const params: Record<string, string> = {
        page: String(pageNumber),
        pageSize: String(pageSize),
        filters: JSON.stringify(filters),
      };
      if (sortBy) params.sortBy = sortBy;
      if (sortDir) params.sortDir = sortDir;

      const urlParams = new URLSearchParams(params).toString();
      return await this.service.getLenderLicensesGrid(urlParams);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error fetching Lender Licenses.',
      });
      return {
        data: { rows: [], meta: { total: 0, pageNumber: 1 } },
        headers: {},
      };
    }
  }

  *resetAndFetchGridData() {
    this.lenderLicensesGridStore.reset();
    yield this.lenderLicensesGridStore.fetchGridData();
  }

  *fetchOnlyGridData() {
    yield this.lenderLicensesGridStore.fetchGridData();
  }

  *previewLicenseDocument(lenderDocumentId: number, title: string) {
    try {
      const response = yield this.service.downloadLenderDocument(lenderDocumentId);
      this.documentPreview = {
        title,
        data: response.data,
        headers: response.headers,
      };
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error loading document preview.',
      });
    }
  }

  abstract reset(): void;
}
